import { useState, useMemo } from 'react'
import { products, categories, formatPrice, type CatalogProduct } from '@platform/products'
import clsx from 'clsx'

export interface OrderItem {
  /**
   * Unieke id per regel. Custom-size producten (ondertapijt, inloopmatten)
   * kunnen meerdere keren in de bestelling voorkomen — bijv. 4 ondertapijten
   * met verschillende maten — dus we kunnen niet op productId dedupliceren.
   */
  lineId: string
  productId: string
  name: string
  sfProductCode: string
  quantity: number
  priceInclBtw: number  // cents (per stuk)
  weight: number
  customSize?: { width: number; length: number }
}

interface Props {
  value: OrderItem[]
  onChange: (items: OrderItem[]) => void
}

export function ProductSelector({ value, onChange }: Props) {
  const [activeCategory, setActiveCategory] = useState<string>('all')
  const [search, setSearch] = useState('')
  const [customSizes, setCustomSizes] = useState<Record<string, { width: number; length: number }>>({})

  // Filter products that are orderable (exclude apparaten-verhuur)
  const orderableProducts = useMemo(() =>
    products.filter((p) => p.category !== 'apparaten-verhuur'),
    []
  )

  const filteredProducts = useMemo(() => {
    let result = orderableProducts
    if (activeCategory !== 'all') {
      result = result.filter((p) => p.category === activeCategory)
    }
    if (search) {
      const q = search.toLowerCase()
      result = result.filter((p) =>
        p.name.toLowerCase().includes(q) ||
        p.brand.toLowerCase().includes(q)
      )
    }
    return result
  }, [orderableProducts, activeCategory, search])

  const activeCategories = useMemo(() =>
    categories.filter((c) => c.id !== 'apparaten-verhuur'),
    []
  )

  const newLineId = () =>
    (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`

  // Vaste-prijs producten: hoeveelheid op één regel.
  const getSimpleQuantity = (productId: string) =>
    value.find((i) => i.productId === productId)?.quantity || 0

  // Custom-size producten: aantal losse regels (elk met eigen maat).
  const getCustomLineCount = (productId: string) =>
    value.filter((i) => i.productId === productId).length

  const calculateCustomPrice = (product: CatalogProduct, width: number, length: number): number => {
    if (!product.customPricing) return 0
    const areaM2 = (width / 100) * (length / 100)
    return Math.round(product.customPricing.pricePerUnit * areaM2)
  }

  // Vaste-prijs product toevoegen: bestaande regel ophogen of nieuwe regel maken.
  const addSimpleItem = (product: CatalogProduct) => {
    const existing = value.find((i) => i.productId === product.id)
    if (existing) {
      onChange(value.map((i) =>
        i.productId === product.id ? { ...i, quantity: i.quantity + 1 } : i
      ))
    } else {
      onChange([...value, {
        lineId: newLineId(),
        productId: product.id,
        name: product.name,
        sfProductCode: product.sfProductCode,
        quantity: 1,
        priceInclBtw: product.price || 0,
        weight: product.weight,
      }])
    }
  }

  // Custom-size product: elke maat is een eigen regel (quantity 1). Na toevoegen
  // legen we de maatvelden zodat direct de volgende maat ingevuld kan worden.
  const addCustomLine = (product: CatalogProduct) => {
    const size = customSizes[product.id]
    if (!size || !size.width || !size.length) return
    onChange([...value, {
      lineId: newLineId(),
      productId: product.id,
      name: product.name,
      sfProductCode: product.sfProductCode,
      quantity: 1,
      priceInclBtw: calculateCustomPrice(product, size.width, size.length),
      weight: product.weight,
      customSize: size,
    }])
    setCustomSizes((prev) => {
      const next = { ...prev }
      delete next[product.id]
      return next
    })
  }

  const decrementSimpleItem = (productId: string) => {
    const existing = value.find((i) => i.productId === productId)
    if (!existing) return
    if (existing.quantity <= 1) {
      onChange(value.filter((i) => i.productId !== productId))
    } else {
      onChange(value.map((i) =>
        i.productId === productId ? { ...i, quantity: i.quantity - 1 } : i
      ))
    }
  }

  const removeLine = (lineId: string) => {
    onChange(value.filter((i) => i.lineId !== lineId))
  }

  const updateCustomSize = (productId: string, field: 'width' | 'length', val: number) => {
    setCustomSizes((prev) => {
      const existing = prev[productId] || { width: 0, length: 0 }
      return { ...prev, [productId]: { ...existing, [field]: val } }
    })
  }

  const total = value.reduce((sum, i) => sum + i.priceInclBtw * i.quantity, 0)

  return (
    <div className="space-y-4">
      {/* Search + filter */}
      <div className="flex gap-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek product..."
          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        />
      </div>

      {/* Category tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1">
        <button
          onClick={() => setActiveCategory('all')}
          className={clsx(
            'px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors',
            activeCategory === 'all'
              ? 'bg-indigo-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          )}
        >
          Alles ({orderableProducts.length})
        </button>
        {activeCategories.map((cat) => {
          const count = orderableProducts.filter((p) => p.category === cat.id).length
          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={clsx(
                'px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors',
                activeCategory === cat.id
                  ? 'bg-indigo-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              )}
            >
              {cat.name} ({count})
            </button>
          )
        })}
      </div>

      {/* Product grid */}
      <div className="grid grid-cols-2 xl:grid-cols-3 gap-3 max-h-[400px] overflow-y-auto pr-1">
        {filteredProducts.map((product) => {
          const isCustom = product.type === 'custom-size'
          const selectedCount = isCustom
            ? getCustomLineCount(product.id)
            : getSimpleQuantity(product.id)
          const size = customSizes[product.id]
          const customPrice = isCustom && size?.width && size?.length
            ? calculateCustomPrice(product, size.width, size.length)
            : null

          return (
            <div
              key={product.id}
              className={clsx(
                'border rounded-lg p-3 transition-colors',
                selectedCount > 0 ? 'border-indigo-300 bg-indigo-50/50' : 'border-gray-200 hover:border-gray-300'
              )}
            >
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-gray-100 rounded-md flex-shrink-0 flex items-center justify-center overflow-hidden">
                  <img
                    src={`https://www.schoonhuisshop.nl${product.image}`}
                    alt={product.name}
                    className="w-full h-full object-contain"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-main leading-tight truncate">{product.name}</p>
                  <p className="text-xs text-muted">{product.brand}</p>
                  {!isCustom && product.price && (
                    <p className="text-sm font-semibold text-main mt-1">{formatPrice(product.price)}</p>
                  )}
                  {isCustom && product.customPricing && (
                    <p className="text-xs text-muted mt-1">
                      {formatPrice(product.customPricing.pricePerUnit)}/m²
                    </p>
                  )}
                  {product.dropship && (
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-700 mt-1">
                      Dropship
                    </span>
                  )}
                </div>
              </div>

              {/* Custom size inputs */}
              {isCustom && (
                <div className="flex gap-2 mt-2">
                  <input
                    type="number"
                    placeholder="Breedte (cm)"
                    value={size?.width || ''}
                    onChange={(e) => updateCustomSize(product.id, 'width', Number(e.target.value))}
                    className="w-24 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-indigo-500"
                    min={product.customPricing?.minWidth}
                    max={product.customPricing?.maxWidth}
                  />
                  <span className="text-xs text-muted self-center">×</span>
                  <input
                    type="number"
                    placeholder="Lengte (cm)"
                    value={size?.length || ''}
                    onChange={(e) => updateCustomSize(product.id, 'length', Number(e.target.value))}
                    className="w-24 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-indigo-500"
                    min={product.customPricing?.minLength}
                    max={product.customPricing?.maxLength}
                  />
                  {customPrice !== null && (
                    <span className="text-xs font-semibold text-main self-center">
                      {formatPrice(customPrice)}
                    </span>
                  )}
                </div>
              )}

              {/* Add controls — custom-size: elke maat een eigen regel; simpel: +/− hoeveelheid */}
              {isCustom ? (
                <div className="flex items-center gap-2 mt-2">
                  <button
                    onClick={() => addCustomLine(product)}
                    disabled={!size?.width || !size?.length}
                    className={clsx(
                      'flex-1 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors',
                      !size?.width || !size?.length
                        ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                    )}
                  >
                    + Toevoegen
                  </button>
                  {selectedCount > 0 && (
                    <span className="text-xs font-medium text-indigo-600 whitespace-nowrap">
                      {selectedCount}× toegevoegd
                    </span>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-end gap-2 mt-2">
                  {selectedCount > 0 && (
                    <>
                      <button
                        onClick={() => decrementSimpleItem(product.id)}
                        className="w-7 h-7 rounded-md bg-slate-200 text-slate-600 hover:bg-slate-300 flex items-center justify-center text-sm font-bold"
                      >
                        −
                      </button>
                      <span className="w-6 text-center text-sm font-semibold">{selectedCount}</span>
                    </>
                  )}
                  <button
                    onClick={() => addSimpleItem(product)}
                    className="w-7 h-7 rounded-md flex items-center justify-center text-sm font-bold transition-colors bg-indigo-600 text-white hover:bg-indigo-700"
                  >
                    +
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Selected items summary */}
      {value.length > 0 && (
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-main">
              Geselecteerd ({value.reduce((s, i) => s + i.quantity, 0)} items)
            </h4>
            <span className="text-sm font-semibold text-main">{formatPrice(total)}</span>
          </div>
          <div className="space-y-1">
            {value.map((item) => (
              <div key={item.lineId} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-muted truncate">
                  {item.quantity}× {item.name}
                  {item.customSize && ` (${item.customSize.width}×${item.customSize.length}cm)`}
                </span>
                <span className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-main">{formatPrice(item.priceInclBtw * item.quantity)}</span>
                  <button
                    onClick={() => removeLine(item.lineId)}
                    className="text-slate-400 hover:text-red-500 transition-colors text-base leading-none"
                    aria-label="Verwijder regel"
                  >
                    ×
                  </button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
